import { io, Socket } from 'socket.io-client';

// ----------- Type Definitions -----------
export interface SocketEvent {
    event: string;
    data: any;
    timestamp: number;
}

export interface SocketServiceConfig {
    url: string;
    options?: any;
    autoConnect?: boolean;
    reconnection?: boolean;
    reconnectionAttempts?: number;
    reconnectionDelay?: number;
    reconnectionDelayMax?: number;
}

export interface SocketServiceInterface {
    connect(): void;
    disconnect(): void;
    emit(event: string, data?: any): void;
    on(event: string, callback: (data: any) => void): void;
    off(event: string, callback?: (data: any) => void): void;
    isConnected(): boolean;
    getEventHistory(): SocketEvent[];
    clearEventHistory(): void;
}

// ----------- Base Socket Service Class -----------
export abstract class BaseSocketService implements SocketServiceInterface {
    protected socket: Socket | null = null;
    protected eventHistory: SocketEvent[] = [];
    protected eventListeners: Map<string, Set<(data: any) => void>> = new Map();
    protected wrappedCallbacks: Map<(data: any) => void, (data: any) => void> = new Map(); // Track wrapped callbacks for cleanup
    protected config: SocketServiceConfig;
    protected isConnecting: boolean = false;
    protected reconnectAttempts: number = 0;
    protected maxReconnectAttempts: number = 10;
    protected connectionTimeout: NodeJS.Timeout | null = null;
    protected setupTimeout: NodeJS.Timeout | null = null;
    protected areEventListenersSetup: boolean = false;

    constructor(config: SocketServiceConfig) {
        this.config = config;
    }

    // Connect to socket
    connect(): void {
        if (this.socket?.connected) {
            return;
        }

        if (this.isConnecting) {
            return;
        }

        // Clear any existing setup timeout
        if (this.setupTimeout) {
            clearTimeout(this.setupTimeout);
            this.setupTimeout = null;
        }

        this.isConnecting = true;

        try {
            // Get the global socket from the manager instead of creating a new one
            const globalSocket = socketServiceManager.getGlobalSocket();
            
            if (globalSocket) {
                this.socket = globalSocket;
                
                // If socket is already connected, set up listeners immediately
                if (globalSocket.connected) {
                    this.setupEventListeners();
                    this.isConnecting = false;
                    return;
                }
                
                // If socket exists but not connected, wait for connection
                // Set up listeners - they will be triggered when socket connects
                this.setupEventListeners();
                
                // Set a timeout to handle connection failure
                this.connectionTimeout = setTimeout(() => {
                    if (!this.socket?.connected) {
                        this.handleConnectionError(new Error('Connection timeout - socket did not connect within expected time'));
                    }
                }, 20000); // 20 second timeout
                
                // Don't set isConnecting to false yet - wait for actual connection
                return;
            }

            // If no global socket exists, initialize it
            console.log('🔌 No global socket found, initializing...');
            socketServiceManager.initializeGlobalSocket(this.config);
            
            // Poll for global socket creation with retry logic
            let retryCount = 0;
            const maxRetries = 100; // 10 second max (10 * 100ms)
            
            const checkForSocket = () => {
                const newGlobalSocket = socketServiceManager.getGlobalSocket();
                if (newGlobalSocket) {
                    console.log('🔌 Using newly created global socket');
                    this.socket = newGlobalSocket;
                    this.setupEventListeners();
                    
                    // Set connection timeout
                    this.connectionTimeout = setTimeout(() => {
                        if (!this.socket?.connected) {
                            this.handleConnectionError(new Error('Connection timeout - socket did not connect within expected time'));
                        }
                    }, 20000);
                    
                    // If already connected, mark as connected
                    if (newGlobalSocket.connected) {
                        this.isConnecting = false;
                    }
                    // Otherwise, wait for connect event
                } else if (retryCount < maxRetries) {
                    retryCount++;
                    this.setupTimeout = setTimeout(checkForSocket, 100);
                } else {
                    this.handleConnectionError(new Error('Global socket initialization failed - socket not created after retries'));
                }
            };
            
            this.setupTimeout = setTimeout(checkForSocket, 100);

        } catch (error) {
            this.isConnecting = false;
            this.handleConnectionError(error);
        }
    }

    // Disconnect from socket
    disconnect(): void {
        // Clear all timeouts
        if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }
        
        if (this.setupTimeout) {
            clearTimeout(this.setupTimeout);
            this.setupTimeout = null;
        }

        if (this.socket) {
            // Don't disconnect the global socket, just remove our business event listeners
            // Connection lifecycle listeners are managed at manager level
            this.eventListeners.forEach((callbacks, event) => {
                callbacks.forEach(callback => {
                    const wrappedCallback = this.wrappedCallbacks.get(callback);
                    if (wrappedCallback) {
                        this.socket!.off(event, wrappedCallback);
                        this.wrappedCallbacks.delete(callback);
                    } else {
                        // Fallback: try to remove original callback
                        this.socket!.off(event, callback);
                    }
                });
            });
            this.eventListeners.clear();
            this.wrappedCallbacks.clear();
            
            this.socket = null;
            this.isConnecting = false;
            this.reconnectAttempts = 0;
            this.areEventListenersSetup = false;
        }
    }

    // Emit event with better error handling
    emit(event: string, data?: any): void {
        if (!this.socket) {
            // Only queue subscribe events, not unsubscribe (unsubscribe should fail fast)
            if (event === 'subscribe') {
                this.queueEventForLater(event, data);
            } else {
                console.warn(`⚠️ Cannot emit '${event}' - socket not initialized`);
            }
            return;
        }

        if (this.socket.connected) {
            try {
                this.socket.emit(event, data);
                this.addEventToHistory(`emit:${event}`, data);
            } catch (error) {
                // Only queue subscribe events on error, not unsubscribe
                if (event === 'subscribe') {
                    this.queueEventForLater(event, data);
                } else {
                    console.error(`❌ Error emitting '${event}':`, error);
                }
            }
        } else {
            // Only queue subscribe events when disconnected, not unsubscribe
            // Unsubscribe events should fail fast if socket is disconnected
            if (event === 'subscribe') {
                this.queueEventForLater(event, data);
            } else {
                console.warn(`⚠️ Cannot emit '${event}' - socket not connected`);
            }
        }
    }

    // Listen to event with validation
    on(event: string, callback: (data: any) => void): void {
        if (!event || typeof event !== 'string') {
            return;
        }

        if (typeof callback !== 'function') {
            return;
        }

        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        
        // Check if callback is already registered for this event
        if (this.eventListeners.get(event)!.has(callback)) {
            return; // Already registered, avoid duplicate
        }
        
        this.eventListeners.get(event)!.add(callback);

        if (this.socket) {
            // Wrap callback to handle errors
            const wrappedCallback = (data: any) => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`❌ Error in event listener for '${event}':`, error);
                }
            };
            
            // Store wrapped callback for proper cleanup
            this.wrappedCallbacks.set(callback, wrappedCallback);
            
            // Attach listener
            this.socket.on(event, wrappedCallback);
        }
    }

    // Remove event listener
    off(event: string, callback?: (data: any) => void): void {
        if (callback) {
            this.eventListeners.get(event)?.delete(callback);
            if (this.socket) {
                // Remove using wrapped callback if it exists
                const wrappedCallback = this.wrappedCallbacks.get(callback);
                if (wrappedCallback) {
                    this.socket.off(event, wrappedCallback);
                    this.wrappedCallbacks.delete(callback);
                } else {
                    // Fallback: try to remove original callback
                    this.socket.off(event, callback);
                }
            }
        } else {
            // Remove all listeners for this event
            const callbacks = this.eventListeners.get(event);
            if (callbacks && this.socket) {
                callbacks.forEach(cb => {
                    const wrappedCallback = this.wrappedCallbacks.get(cb);
                    if (wrappedCallback) {
                        this.socket!.off(event, wrappedCallback);
                        this.wrappedCallbacks.delete(cb);
                    }
                });
            }
            this.eventListeners.delete(event);
            if (this.socket) {
                this.socket.off(event);
            }
        }
    }

    // Check if connected
    isConnected(): boolean {
        return this.socket?.connected ?? false;
    }

    // Get event history
    getEventHistory(): SocketEvent[] {
        return [...this.eventHistory];
    }

    // Clear event history
    clearEventHistory(): void {
        this.eventHistory = [];
    }

    // Add event to history
    protected addEventToHistory(event: string, data: any): void {
        const socketEvent: SocketEvent = {
            event,
            data,
            timestamp: Date.now(),
        };
        this.eventHistory = [...this.eventHistory.slice(-99), socketEvent]; // Keep last 100 events
    }

    // Queue event for later emission when socket reconnects
    private queuedEvents: Array<{ event: string; data: any }> = [];
    
    private queueEventForLater(event: string, data: any): void {
        this.queuedEvents.push({ event, data });
        // Keep only last 50 queued events
        if (this.queuedEvents.length > 50) {
            this.queuedEvents = this.queuedEvents.slice(-50);
        }
    }

    // Emit queued events when socket reconnects
    private emitQueuedEvents(): void {
        if (this.queuedEvents.length === 0) return;

        console.log(`📤 Emitting ${this.queuedEvents.length} queued events...`);
        while (this.queuedEvents.length > 0) {
            const queuedEvent = this.queuedEvents.shift();
            if (queuedEvent && this.socket?.connected) {
                try {
                    this.socket.emit(queuedEvent.event, queuedEvent.data);
                    this.addEventToHistory(`emit:${queuedEvent.event}`, queuedEvent.data);
                } catch (error) {
                    console.error(`❌ Error emitting queued event '${queuedEvent.event}':`, error);
                }
            }
        }
    }

    // Handle connection errors
    // NOTE: We let Socket.IO handle reconnection automatically
    // This method only logs errors and updates state
    protected handleConnectionError(error: any): void {
        this.addEventToHistory('connection_error', { error: error.message || error });
        
        if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }
        
        if (this.setupTimeout) {
            clearTimeout(this.setupTimeout);
            this.setupTimeout = null;
        }

        this.isConnecting = false;
        // Socket.IO will handle reconnection automatically if enabled
        // We don't manually call connect() here to avoid conflicts
    }

    // Setup event listeners
    // NOTE: Connection lifecycle events (connect/disconnect/reconnect) are handled at manager level
    // Services only set up business event listeners here
    private setupEventListeners(): void {
        if (!this.socket) return;
        
        // Prevent duplicate setup
        if (this.areEventListenersSetup) {
            // Only re-attach custom business event listeners
            this.eventListeners.forEach((callbacks, event) => {
                callbacks.forEach(callback => {
                    let wrappedCallback = this.wrappedCallbacks.get(callback);
                    if (!wrappedCallback) {
                        wrappedCallback = (data: any) => {
                            try {
                                callback(data);
                            } catch (error) {
                                console.error(`❌ Error in event listener for '${event}':`, error);
                            }
                        };
                        this.wrappedCallbacks.set(callback, wrappedCallback);
                    }
                    // Only attach if not already attached (check to avoid duplicates)
                    if (!this.socket!.hasListeners(event) || !this.wrappedCallbacks.has(callback)) {
                        this.socket!.on(event, wrappedCallback);
                    }
                });
            });
            return;
        }

        // Services do NOT set up connection lifecycle listeners here
        // Those are handled at manager level to prevent duplicates
        
        // Only set up business event listeners
        this.eventListeners.forEach((callbacks, event) => {
            callbacks.forEach(callback => {
                // Get or create wrapped callback
                let wrappedCallback = this.wrappedCallbacks.get(callback);
                if (!wrappedCallback) {
                    wrappedCallback = (data: any) => {
                        try {
                            callback(data);
                        } catch (error) {
                            console.error(`❌ Error in event listener for '${event}':`, error);
                        }
                    };
                    this.wrappedCallbacks.set(callback, wrappedCallback);
                }
                // Attach business event listener
                this.socket!.on(event, wrappedCallback);
            });
        });

        this.areEventListenersSetup = true;
    }

    // Hook method called when socket connects or reconnects
    // This is called by the manager when connection events occur
    // Subclasses can override this to restore subscriptions, re-register listeners, etc.
    protected onReconnect(): void {
        // Emit queued events when reconnected
        this.emitQueuedEvents();
        // Default implementation does nothing else
        // Subclasses should override this to restore their state
    }

    // Abstract methods that subclasses must implement
    abstract getServiceName(): string;
    abstract getEvents(): string[];
}

// ----------- Socket Service Manager -----------
class SocketServiceManager {
    private services: Map<string, BaseSocketService> = new Map();
    private globalSocket: Socket | null = null;
    private globalConfig: SocketServiceConfig | null = null;
    private isGlobalSocketConnecting: boolean = false;
    private reconnectionCheckInterval: NodeJS.Timeout | null = null;

    // Initialize global socket connection
    initializeGlobalSocket(config: SocketServiceConfig): void {
        if (this.globalSocket?.connected) {
            return;
        }

        if (this.isGlobalSocketConnecting) {
            return;
        }

        this.globalConfig = config;
        this.isGlobalSocketConnecting = true;
        
        try {
            // DO NOT convert URL - Socket.IO handles http://https:// internally
            // Converting to ws://wss:// breaks proxies, load balancers, and Cloudflare
            const socketUrl = config.url;

            this.globalSocket = io(socketUrl, {
                path: '/ws/', // Backend expects this path
                autoConnect: config.autoConnect ?? true,
                reconnection: config.reconnection ?? true, // Let Socket.IO handle reconnection
                reconnectionAttempts: config.reconnectionAttempts ?? Infinity, // Infinite attempts - never give up
                reconnectionDelay: config.reconnectionDelay ?? 1000, // Start with 1 second
                reconnectionDelayMax: config.reconnectionDelayMax ?? 10000, // Max 10 seconds between attempts (increased)
                randomizationFactor: 0.5, // Add some randomness to prevent thundering herd
                transports: ['websocket'], // Use websocket only to avoid polling upgrade issues
                timeout: 20000,
                forceNew: true, // Force new connection - manager owns this socket exclusively
                // CRITICAL: Ensure reconnection never stops
                // Socket.IO will keep trying indefinitely with exponential backoff
                ...config.options
            });

            // Set up global socket event listeners (connection lifecycle only)
            this.setupGlobalSocketListeners();
            
            // CRITICAL: Start periodic reconnection check to ensure infinite reconnection
            this.startReconnectionCheck();

        } catch (error) {
            this.isGlobalSocketConnecting = false;
            console.error('❌ Error initializing global socket:', error);
        }
    }
    
    // Periodic check to ensure reconnection continues indefinitely
    private startReconnectionCheck(): void {
        // Clear any existing interval
        if (this.reconnectionCheckInterval) {
            clearInterval(this.reconnectionCheckInterval);
        }
        
        // Check every 10 seconds if socket is disconnected and not reconnecting
        this.reconnectionCheckInterval = setInterval(() => {
            if (this.globalSocket && !this.globalSocket.connected && !this.isGlobalSocketConnecting) {
                // Socket is disconnected and not actively connecting
                // Check if Socket.IO is still trying to reconnect
                const isReconnecting = (this.globalSocket as any)._reconnecting || false;
                
                if (!isReconnecting) {
                    // Socket.IO has stopped trying - manually trigger reconnection
                    console.log('🔄 Socket.IO stopped reconnecting - manually triggering reconnect...');
                    this.globalSocket.connect();
                }
            }
        }, 10000); // Check every 10 seconds
    }
    
    private stopReconnectionCheck(): void {
        if (this.reconnectionCheckInterval) {
            clearInterval(this.reconnectionCheckInterval);
            this.reconnectionCheckInterval = null;
        }
    }

    // Setup global socket event listeners
    // These are the ONLY connection lifecycle listeners - services should NOT add their own
    private setupGlobalSocketListeners(): void {
        if (!this.globalSocket) return;

        // Prevent duplicate setup
        if (this.globalSocket.hasListeners('connect')) {
            return; // Already set up
        }

        this.globalSocket.on('connect', () => {
            this.isGlobalSocketConnecting = false;
            console.log('✅ Global socket connected');
            
            // Notify all services about connection
            this.services.forEach(service => {
                // Reset service connection state
                if ((service as any).isConnecting !== undefined) {
                    (service as any).isConnecting = false;
                }
                if ((service as any).connectionTimeout) {
                    clearTimeout((service as any).connectionTimeout);
                    (service as any).connectionTimeout = null;
                }
                
                // Services handle their own business logic via onReconnect hook
                // Call onReconnect for BOTH initial connect and reconnect
                if (service.isConnected()) {
                    // Trigger service-level reconnection logic
                    // This will restore subscriptions, emit queued events, etc.
                    (service as any).onReconnect?.();
                }
            });
        });

        this.globalSocket.on('disconnect', (reason: string) => {
            this.isGlobalSocketConnecting = false;
            console.log('❌ Global socket disconnected:', reason);
            
            // CRITICAL: If disconnect is due to transport close or server shutdown,
            // ensure reconnection continues. Socket.IO should handle this automatically,
            // but we add a safety net to manually trigger if needed
            if (reason === 'transport close' || reason === 'io server disconnect') {
                // Socket.IO will automatically attempt reconnection
                // But if it doesn't, we'll manually trigger after a delay
                setTimeout(() => {
                    if (this.globalSocket && !this.globalSocket.connected && !this.globalSocket.active) {
                        console.log('🔄 Manually triggering reconnection after disconnect...');
                        this.globalSocket.connect();
                    }
                }, 2000);
            }
        });

        this.globalSocket.on('connect_error', (error: any) => {
            this.isGlobalSocketConnecting = false;
            console.error('❌ Global socket connection error:', error);
            // Socket.IO will handle reconnection automatically with infinite attempts
            // No need to manually trigger - it will keep trying
        });

        this.globalSocket.on('reconnect', (attemptNumber: number) => {
            this.isGlobalSocketConnecting = false;
            console.log(`🔄 Global socket reconnected (attempt ${attemptNumber})`);
            // Notify all services about reconnection
            this.services.forEach(service => {
                // Reset service connection state
                if ((service as any).isConnecting !== undefined) {
                    (service as any).isConnecting = false;
                }
                if ((service as any).connectionTimeout) {
                    clearTimeout((service as any).connectionTimeout);
                    (service as any).connectionTimeout = null;
                }
                
                // IMPORTANT: Call onReconnect() to restore subscriptions
                // Increased delay to ensure socket is fully ready after reconnect
                setTimeout(() => {
                    if (service.isConnected()) {
                        console.log(`🔄 Calling onReconnect() for service: ${service.getServiceName()}`);
                        (service as any).onReconnect?.();
                    } else {
                        // If not connected yet, retry after a delay
                        setTimeout(() => {
                            if (service.isConnected()) {
                                console.log(`🔄 Retrying onReconnect() for service: ${service.getServiceName()}`);
                                (service as any).onReconnect?.();
                            }
                        }, 500);
                    }
                }, 200); // Increased delay to ensure socket is ready
            });
        });

        this.globalSocket.on('reconnect_error', (error: any) => {
            console.error('❌ Global socket reconnect error:', error);
            // Socket.IO will continue trying
        });

        this.globalSocket.on('reconnect_failed', () => {
            // Note: With reconnectionAttempts: Infinity, this should never fire
            // But if it does, we'll manually trigger reconnection
            this.isGlobalSocketConnecting = false;
            console.error('❌ Global socket reconnect failed - attempting manual reconnect...');
            
            // Manually trigger reconnection attempt
            if (this.globalSocket && !this.globalSocket.connected) {
                setTimeout(() => {
                    if (this.globalSocket && !this.globalSocket.connected) {
                        this.globalSocket.connect();
                    }
                }, 2000);
            }
        });

        // Listen for pong response from backend
        this.globalSocket.on('pong', (data: any) => {
            console.log('🏓 Pong response received from backend:', data);
        });
    }

    // Register a service
    registerService(service: BaseSocketService): void {
        this.services.set(service.getServiceName(), service);
    }

    // Get a service by name
    getService<T extends BaseSocketService>(serviceName: string): T | undefined {
        return this.services.get(serviceName) as T;
    }

    // Get all services
    getAllServices(): BaseSocketService[] {
        return Array.from(this.services.values());
    }

    // Disconnect all services
    disconnectAll(): void {
        this.services.forEach(service => service.disconnect());
        if (this.globalSocket) {
            this.globalSocket.disconnect();
            this.globalSocket = null;
        }
        this.isGlobalSocketConnecting = false;
        this.stopReconnectionCheck();
    }

    // Get global socket
    getGlobalSocket(): Socket | null {
        return this.globalSocket;
    }

    // Get global config
    getGlobalConfig(): SocketServiceConfig | null {
        return this.globalConfig;
    }

    // Check if any service is connected
    isAnyServiceConnected(): boolean {
        return this.globalSocket?.connected ?? false;
    }

    // Get connection status of all services
    getServicesConnectionStatus(): Record<string, boolean> {
        const status: Record<string, boolean> = {};
        this.services.forEach((service, name) => {
            status[name] = service.isConnected();
        });
        return status;
    }

    // Check if global socket is connecting
    getGlobalSocketConnectingStatus(): boolean {
        return this.isGlobalSocketConnecting;
    }
}

// Create singleton instance
export const socketServiceManager = new SocketServiceManager();

// Export the manager as default
export default socketServiceManager; 